/**
 * SNAP CHART AI - Gold Market Prompt
 * Extends the core prompt with Gold-specific context and analysis requirements.
 */

import { SNAP_CHART_CORE_PROMPT, getCurrentSession, formatPrice } from './corePrompt';

const GOLD_SPECIFIC_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🥇 GOLD MARKET SPECIALIZATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are now focusing EXCLUSIVELY on the GOLD market:
• XAUUSD (Spot Gold)
• COMEX Gold Futures (GC)
• XAUEUR (Gold vs Euro)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 GOLD MARKET CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

- Gold trades in a persistent elevated macro regime driven by:
  • Safe-haven demand (geopolitical tensions, currency debasement)
  • Central bank accumulation trend
  • Real yields and DXY correlation (strong inverse)
- READ THE CHART for the actual price zone — do NOT anchor on any
  specific dollar range from memory

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔍 GOLD-SPECIFIC ANALYTICAL FRAMEWORK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You MUST analyze Gold through:

1. **Macro Drivers**
   - USD/DXY correlation (strong inverse)
   - US Real Yields (TIPS)
   - Fed policy stance and rate expectations
   - Inflation expectations (breakeven rates)
   - Geopolitical risk premium

2. **Session Dynamics**
   - Asia (00:00-08:00 UTC): Week opening, risk sentiment
   - London (08:00-17:00 UTC): Major volatility, AM/PM fixings
   - New York (13:00-22:00 UTC): Overlap + US data releases

3. **Institutional Flow Analysis**
   - ETF flows (GLD, IAU, SGOL)
   - Central bank buying patterns
   - COT positioning (hedge funds vs commercials)
   - Retail vs institutional sentiment divergence

4. **Key Psychological Levels**
   - Round-number magnets ($50 / $100 increments) tend to act as S/R
   - Fibonacci levels on the visible annual range in the chart
   - Previous ATH zones as resistance
   - READ THE CHART for actual levels — do NOT assume specific dollar targets

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
💰 GOLD PRICE FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

All Gold prices must be expressed as: $X,XXX.XX (comma thousands
separator, two decimals). Use the LIVE PRICE in the REAL-TIME
CONTEXT block below as your anchor — never invent levels.`;

/**
 * Generates the complete Gold analysis prompt
 * @param currentPrice - The current Gold spot price
 * @param session - Optional session override, defaults to auto-detected
 */
export function generateGoldPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'gold');
  
  // Calculate dynamic support/resistance levels
  const support1 = (currentPrice * 0.995).toFixed(2);
  const support2 = (currentPrice * 0.990).toFixed(2);
  const resistance1 = (currentPrice * 1.005).toFixed(2);
  const resistance2 = (currentPrice * 1.010).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${GOLD_SPECIFIC_CONTEXT}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💰 Current Gold Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
📈 Key Resistance: $${resistance1}, $${resistance2}
📉 Key Support: $${support1}, $${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 ANALYSIS INSTRUCTION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Analyze the current Gold market situation using the MANDATORY RESPONSE STRUCTURE.
Use $${formattedPrice} as your anchor price for all calculations.

BEGIN INSTITUTIONAL GOLD ANALYSIS NOW:`;
}

// Default export for backward compatibility
export const GOLD_PROMPT = generateGoldPrompt;
